import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { categories, collections } from '../data/products'
import { supabase } from '../lib/supabaseClient'
import { supabaseAdmin } from '../lib/supabaseAdmin'
import MediaWithShimmer from '../components/MediaWithShimmer'

const Account = () => {
  const { user, signOut, isAdmin } = useAuth()
  const { language } = useLanguage()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('profile')
  const [productType, setProductType] = useState('single') // 'single' or 'set'
  const [setItems, setSetItems] = useState([{ name: '', nameEn: '', nameEs: '', image: '', size: '', color: '' }])
  const [newProduct, setNewProduct] = useState({
    name: '',
    nameEn: '',
    nameEs: '',
    price: '',
    category: 'fashion',
    collection: 'haute-couture',
    description: '',
    descriptionEn: '',
    descriptionEs: '',
    is_hidden: false,
    stock: 0,
    sizes: [],
    colors: [],
    images: [],
    badge: '',
    sku: '',
    brand: '',
    material: '',
    volume: '',
    weight: '',
    dimensions: '',
    country: '',
    notes_top: '',
    notes_heart: '',
    notes_base: '',
    gender: ''
  })
  const [submitError, setSubmitError] = useState('')
  const [submitInfo, setSubmitInfo] = useState('')

  const [allProducts, setAllProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [editSetItems, setEditSetItems] = useState([])

  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [ordersError, setOrdersError] = useState('')

  useEffect(() => {
    let active = true
    const fetchOrders = async () => {
      if (!user?.id && !user?.email) { setOrders([]); setOrdersLoading(false); return }
      try {
        setOrdersLoading(true)
        setOrdersError('')
        // 1) Try secure backend endpoint first
        try {
          const token = localStorage.getItem('auth_token')
          if (token) {
            const resp = await fetch('/api/orders/user', { headers: { Authorization: `Bearer ${token}` } })
            if (resp.ok) {
              const json = await resp.json().catch(()=>({}))
              if (Array.isArray(json?.orders)) {
                if (active) setOrders(json.orders)
                return
              }
            }
          }
        } catch {}

        // 2) Fallback to client-side Supabase queries and merge
        const results = []
        const seen = new Set()

        if (user?.id) {
          const { data: byId, error: errById } = await supabase
            .from('orders')
            .select('id, order_number, total, status, created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
          if (errById) throw errById
          ;(byId || []).forEach(o => { if (!seen.has(o.id)) { seen.add(o.id); results.push(o) } })
        }

        if (user?.email) {
          const { data: byEmail, error: errByEmail } = await supabase
            .from('orders')
            .select('id, order_number, total, status, created_at')
            .eq('email', user.email)
            .order('created_at', { ascending: false })
          if (errByEmail) throw errByEmail
          ;(byEmail || []).forEach(o => { if (!seen.has(o.id)) { seen.add(o.id); results.push(o) } })
        }

        results.sort((a,b)=> new Date(b.created_at) - new Date(a.created_at))
        if (active) setOrders(results)
      } catch (e) {
        if (active) setOrdersError(e?.message || 'Failed to load orders')
      } finally {
        if (active) setOrdersLoading(false)
      }
    }
    fetchOrders()
    return () => { active = false }
  }, [user?.id, user?.email])

  useEffect(() => {
    if (isAdmin && activeTab === 'manage-products') {
      fetchAllProducts()
    }
  }, [isAdmin, activeTab])

  const fetchAllProducts = async () => {
    setLoadingProducts(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('id', { ascending: false })
      if (error) throw error
      const normalized = (data || []).map(p => ({
        ...p,
        nameEn: p.nameEn ?? p.nameen ?? '',
        nameEs: p.nameEs ?? p.namees ?? '',
        descriptionEn: p.descriptionEn ?? p.descriptionen ?? '',
        descriptionEs: p.descriptionEs ?? p.descriptiones ?? ''
      }))
      setAllProducts(normalized)
    } catch (e) {
      console.error('Failed to load products:', e)
    } finally {
      setLoadingProducts(false)
    }
  }

  const handleEditProduct = (product) => {
    setEditingProduct(product.id)
    setEditForm({
      name: product.name || '',
      nameEn: product.nameEn || '',
      nameEs: product.nameEs || '',
      price: product.price || '',
      category: product.category || 'fashion',
      collection: product.collection || 'haute-couture',
      description: product.description || '',
      descriptionEn: product.descriptionEn || '',
      descriptionEs: product.descriptionEs || '',
      is_hidden: Boolean(product.is_hidden),
      is_set: Boolean(product.is_set),
      stock: product.stock || 0,
      sizes: product.sizes || [],
      colors: product.colors || [],
      images: product.images || [],
      imagesText: Array.isArray(product.images) ? product.images.join(', ') : '',
      sizesText: Array.isArray(product.sizes) ? product.sizes.join(', ') : '',
      colorsText: Array.isArray(product.colors) ? product.colors.join(', ') : '',
      badge: product.badge || '',
      sku: product.sku || '',
      brand: product.brand || '',
      material: product.material || '',
      volume: product.volume || '',
      weight: product.weight || '',
      dimensions: product.dimensions || '',
      country: product.country || '',
      notes_top: product.notes_top || '',
      notes_heart: product.notes_heart || '',
      notes_base: product.notes_base || '',
      gender: product.gender || ''
    })
    setEditSetItems(product.is_set && product.set_items ? product.set_items : [])
  }

  const handleUpdateProduct = async (productId) => {
    try {
      const payload = {
        name: editForm.name,
        nameen: editForm.nameEn,
        namees: editForm.nameEs,
        price: Number(editForm.price),
        stock: Number(editForm.stock),
        category: editForm.category,
        collection: editForm.collection,
        description: editForm.description,
        descriptionen: editForm.descriptionEn,
        descriptiones: editForm.descriptionEs,
        is_hidden: editForm.is_hidden,
        is_set: editForm.is_set,
        set_items: editForm.is_set ? editSetItems : null,
        sizes: editForm.sizesText ? editForm.sizesText.split(',').map(s=>s.trim()).filter(Boolean) : [],
        colors: editForm.colorsText ? editForm.colorsText.split(',').map(s=>s.trim()).filter(Boolean) : [],
        images: editForm.imagesText ? editForm.imagesText.split(',').map(s=>s.trim()).filter(Boolean) : [],
        badge: editForm.badge,
        sku: editForm.sku,
        brand: editForm.brand,
        material: editForm.material,
        volume: editForm.volume,
        weight: editForm.weight,
        dimensions: editForm.dimensions,
        country: editForm.country,
        notes_top: editForm.notes_top,
        notes_heart: editForm.notes_heart,
        notes_base: editForm.notes_base,
        gender: editForm.gender
      }
      
      console.log('Updating product with payload:', payload)
      
      // Use admin client to bypass RLS
      const client = supabaseAdmin || supabase
      const { data, error } = await client
        .from('products')
        .update(payload)
        .eq('id', productId)
        .select()
      
      console.log('Update result:', { data, error })
      
      if (error) throw error
      
      await fetchAllProducts()
      setEditingProduct(null)
      setEditForm(null)
      setSubmitInfo(language === 'ru' ? 'Товар обновлен' : language === 'es' ? 'Producto actualizado' : 'Product updated')
      setTimeout(() => setSubmitInfo(''), 3000)
    } catch (e) {
      console.error('Update error:', e)
      setSubmitError(e.message)
      setTimeout(() => setSubmitError(''), 3000)
    }
  }

  const handleDeleteProduct = async (productId) => {
    if (!confirm(language === 'ru' ? 'Удалить товар?' : language === 'es' ? '¿Eliminar producto?' : 'Delete product?')) return
    
    try {
      console.log('Deleting product with ID:', productId)
      console.log('Current user from context:', user?.id)
      console.log('Is admin from context:', isAdmin)
      
      // Check profile in database
      if (user?.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single()
        
        console.log('Profile from DB:', profile)
      }
      
      // Try to delete
      const { data, error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId)
        .select()
      
      console.log('Delete result:', { data, error })
      
      if (error) throw error
      
      if (!data || data.length === 0) {
        throw new Error('RLS policy blocked deletion. In Supabase dashboard, go to profiles table and set is_admin = true for your user.')
      }
      
      await fetchAllProducts()
      setSubmitInfo(language === 'ru' ? 'Товар удален' : language === 'es' ? 'Producto eliminado' : 'Product deleted')
      setTimeout(() => setSubmitInfo(''), 3000)
    } catch (e) {
      console.error('Delete error:', e)
      setSubmitError(e.message)
      setTimeout(() => setSubmitError(''), 3000)
    }
  }

  const handleCreateProduct = async (e) => {
    e.preventDefault()
    setSubmitError('')
    setSubmitInfo('')
    
    try {
      const payload = {
        name: newProduct.name,
        nameen: newProduct.nameEn,
        namees: newProduct.nameEs,
        price: Number(newProduct.price),
        stock: Number(newProduct.stock) || 0,
        category: newProduct.category,
        collection: newProduct.collection,
        description: newProduct.description,
        descriptionen: newProduct.descriptionEn,
        descriptiones: newProduct.descriptionEs,
        is_set: productType === 'set',
        set_items: productType === 'set' ? setItems.filter(item => item.name.trim()) : null,
        is_hidden: Boolean(newProduct.is_hidden),
        sizes: Array.isArray(newProduct.sizes) ? newProduct.sizes : [],
        colors: Array.isArray(newProduct.colors) ? newProduct.colors : [],
        images: Array.isArray(newProduct.images) ? newProduct.images : [],
        badge: newProduct.badge,
        sku: newProduct.sku,
        brand: newProduct.brand,
        material: newProduct.material,
        volume: newProduct.volume,
        weight: newProduct.weight,
        dimensions: newProduct.dimensions,
        country: newProduct.country,
        notes_top: newProduct.notes_top,
        notes_heart: newProduct.notes_heart,
        notes_base: newProduct.notes_base,
        gender: newProduct.gender
      }
      
      // Use admin client to bypass RLS
      const client = supabaseAdmin || supabase
      const { error } = await client
        .from('products')
        .insert([payload])
      
      if (error) throw error
      
      setSubmitInfo(language==='ru'?'Товар создан':'Product created')
      setNewProduct({
        name: '', nameEn: '', nameEs: '', price: '', category: 'fashion', collection: 'haute-couture',
        description: '', descriptionEn: '', descriptionEs: '', is_hidden: false,
        stock: 0, sizes: [], colors: [], images: [], badge: '', sku: '', brand: '', material: '',
        volume: '', weight: '', dimensions: '', country: '', notes_top: '', notes_heart: '', notes_base: '', gender: ''
      })
      setSetItems([{ name: '', nameEn: '', nameEs: '', image: '', size: '', color: '' }])
      setProductType('single')
      await fetchAllProducts()
    } catch (e) {
      setSubmitError(e?.message || 'Ошибка создания товара')
    }
  }

  const tabs = [
    { id: 'profile', label: { ru: 'Профиль', en: 'Profile', es: 'Perfil' }, icon: 'ri-user-3-line' },
    { id: 'orders', label: { ru: 'Заказы', en: 'Orders', es: 'Pedidos' }, icon: 'ri-shopping-bag-3-line' },
    ...(isAdmin ? [
      { id: 'add-product', label: { ru: 'Добавить товар', en: 'Add Product', es: 'Añadir producto' }, icon: 'ri-add-circle-line' },
      { id: 'manage-products', label: { ru: 'Управление товарами', en: 'Manage Products', es: 'Gestionar productos' }, icon: 'ri-edit-box-line' }
    ] : [])
  ]

  return (
    <div className="bg-white pt-32 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        {/* Mobile Menu */}
        <div className="lg:hidden mb-8">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full p-4 border border-gray-200 rounded montserrat text-sm bg-white"
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.label[language]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Menu - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-32 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-4 py-3 montserrat text-sm transition-colors flex items-center gap-3 ${
                    activeTab === tab.id
                      ? 'bg-black text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${tab.icon} text-lg`}></i>
                  {tab.label[language]}
                </button>
              ))}
              <div className="pt-4 border-t border-gray-200 mt-4">
                <button
                  onClick={async ()=>{ try { await signOut() } finally { navigate('/') } }}
                  className="w-full text-left px-4 py-3 montserrat text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3"
                >
                  <i className="ri-logout-box-line text-lg"></i>
                  {language === 'ru' ? 'Выйти' : language === 'es' ? 'Cerrar sesión' : 'Sign Out'}
                </button>
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Tab Content */}
            <div>
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                  <div>
                    <h3 className="text-lg font-medium mb-1 playfair">
                      {language === 'ru' ? 'Информация профиля' : language === 'es' ? 'Información del perfil' : 'Profile Information'}
                    </h3>
                    <div className="text-sm text-gray-600 montserrat mb-3">
                      {language === 'ru' ? 'Ваши личные данные' : language === 'es' ? 'Tus datos personales' : 'Your personal details'}
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <i className="ri-mail-line text-gray-400"></i>
                          <span className="text-gray-600 montserrat">Email:</span>
                          <span className="font-medium montserrat">{user?.email || '—'}</span>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-2 text-sm">
                            <i className="ri-shield-star-line text-amber-500"></i>
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium montserrat">
                              {language === 'ru' ? 'Администратор' : language === 'es' ? 'Administrador' : 'Administrator'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                </div>

                <button
                  className="w-full px-5 py-3.5 bg-black text-white rounded-lg montserrat hover:bg-neutral-900 transition-colors flex items-center justify-center gap-2"
                  onClick={async ()=>{ try { await signOut() } finally { navigate('/') } }}
                >
                  <i className="ri-logout-box-line"></i>
                  {language === 'ru' ? 'Выйти' : language === 'es' ? 'Cerrar sesión' : 'Sign Out'}
                </button>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4 animate-fadeIn">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-medium playfair">
                    {language === 'ru' ? 'Мои заказы' : language === 'es' ? 'Mis pedidos' : 'My Orders'}
                  </h2>
                  <span className="text-sm text-gray-500 montserrat">
                    {language === 'ru' ? `Всего: ${orders.length}` : language === 'es' ? `Total: ${orders.length}` : `Total: ${orders.length}`}
                  </span>
                </div>

                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  {ordersLoading ? (
                    <div className="p-8 text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                      <p className="mt-3 text-sm text-gray-600 montserrat">
                        {language === 'ru' ? 'Загрузка...' : language === 'es' ? 'Cargando...' : 'Loading...'}
                      </p>
                    </div>
                  ) : ordersError ? (
                    <div className="p-8 text-center">
                      <i className="ri-error-warning-line text-4xl text-red-500 mb-3"></i>
                      <p className="text-sm text-red-600 montserrat">{ordersError}</p>
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="p-12 text-center">
                      <i className="ri-shopping-bag-3-line text-5xl text-gray-300 mb-4"></i>
                      <p className="text-gray-600 montserrat">
                        {language === 'ru' ? 'У вас пока нет заказов' : language === 'es' ? 'Aún no tienes pedidos' : 'No orders yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {orders.map((o) => (
                        <div key={o.id} className="p-5 hover:bg-gray-50/50 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-gray-500 montserrat uppercase tracking-wide">
                                  {language === 'ru' ? 'Заказ' : language === 'es' ? 'Pedido' : 'Order'}
                                </span>
                                <span className="text-base font-medium playfair">{o.order_number || `#${o.id}`}</span>
                              </div>
                              <div className="text-xs text-gray-500 montserrat flex items-center gap-1">
                                <i className="ri-calendar-line"></i>
                                {new Date(o.created_at).toLocaleString(language === 'ru' ? 'ru-RU' : language === 'es' ? 'es-ES' : 'en-US')}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`
                                px-3 py-1 rounded-full text-xs font-medium montserrat
                                ${o.status === 'new' ? 'bg-blue-50 text-blue-700 border border-blue-200' : ''}
                                ${o.status === 'processing' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : ''}
                                ${o.status === 'completed' ? 'bg-green-50 text-green-700 border border-green-200' : ''}
                                ${o.status === 'cancelled' ? 'bg-red-50 text-red-700 border border-red-200' : ''}
                              `}>
                                {o.status}
                              </span>
                              <div className="text-lg font-medium playfair whitespace-nowrap">
                                {new Intl.NumberFormat('ru-RU').format(Number(o.total || 0)).replace(/\s/g, '\u00A0') + '\u00A0€'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Add Product Tab (Admin Only) */}
            {activeTab === 'add-product' && isAdmin && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 mb-6">
                  <i className="ri-add-circle-line text-2xl"></i>
                  <h2 className="text-xl font-medium playfair">
                    {language === 'ru' ? 'Создать новый товар' : language === 'es' ? 'Crear nuevo producto' : 'Create New Product'}
                  </h2>
                </div>

                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 montserrat flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {submitError}
                  </div>
                )}
                {submitInfo && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 montserrat flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    {submitInfo}
                  </div>
                )}

                <form onSubmit={handleCreateProduct} className="space-y-6">
                  {/* Product Type Selector */}
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                    <h3 className="text-sm font-medium mb-4 playfair flex items-center gap-2">
                      <i className="ri-stack-line"></i>
                      {language === 'ru' ? 'Тип товара' : language === 'es' ? 'Tipo de producto' : 'Product Type'}
                    </h3>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setProductType('single')}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all montserrat text-sm font-medium ${
                          productType === 'single'
                            ? 'border-black bg-black text-white'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <i className="ri-t-shirt-line mr-2"></i>
                        {language === 'ru' ? 'Одиночный товар' : language === 'es' ? 'Producto individual' : 'Single Item'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductType('set')}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all montserrat text-sm font-medium ${
                          productType === 'set'
                            ? 'border-black bg-black text-white'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <i className="ri-stack-fill mr-2"></i>
                        {language === 'ru' ? 'Комплект' : language === 'es' ? 'Conjunto' : 'Set/Bundle'}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <h3 className="text-sm font-medium mb-4 playfair flex items-center gap-2">
                      <i className="ri-translate-2"></i>
                      {language === 'ru' ? 'Названия' : language === 'es' ? 'Nombres' : 'Names'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        value={newProduct.name}
                        onChange={(e)=>setNewProduct({...newProduct,name:e.target.value})}
                        placeholder={language === 'ru' ? 'Название (RU)' : 'Name (RU)'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        required
                      />
                      <input
                        value={newProduct.nameEn}
                        onChange={(e)=>setNewProduct({...newProduct,nameEn:e.target.value})}
                        placeholder={language === 'ru' ? 'Название (EN)' : 'Name (EN)'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        required
                      />
                      <input
                        value={newProduct.nameEs}
                        onChange={(e)=>setNewProduct({...newProduct,nameEs:e.target.value})}
                        placeholder={language === 'ru' ? 'Название (ES)' : 'Name (ES)'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 montserrat">
                        {language === 'ru' ? 'Цена' : language === 'es' ? 'Precio' : 'Price'}
                      </label>
                      <input
                        value={newProduct.price}
                        onChange={(e)=>setNewProduct({...newProduct,price:e.target.value})}
                        placeholder="0"
                        type="number"
                        className="w-full border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 montserrat">
                        {language === 'ru' ? 'Категория' : language === 'es' ? 'Categoría' : 'Category'}
                      </label>
                      <select
                        value={newProduct.category}
                        onChange={(e)=>setNewProduct({...newProduct,category:e.target.value})}
                        className="w-full border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      >
                        {categories.filter(c=>c.id!=='all').map(c=> (
                          <option key={c.id} value={c.id}>{c.name[language]}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 montserrat">
                        {language === 'ru' ? 'Коллекция' : language === 'es' ? 'Colección' : 'Collection'}
                      </label>
                      <select
                        value={newProduct.collection}
                        onChange={(e)=>setNewProduct({...newProduct,collection:e.target.value})}
                        className="w-full border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      >
                        {collections.filter(c=>c.id!=='all').map(c=> (
                          <option key={c.id} value={c.id}>{c.name[language]}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Hidden Toggle */}
                  <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newProduct.is_hidden}
                        onChange={(e)=>setNewProduct({...newProduct,is_hidden:e.target.checked})}
                        className="w-5 h-5 rounded border-gray-300 text-black focus:ring-2 focus:ring-black"
                      />
                      <div className="flex-1">
                        <div className="font-medium montserrat text-sm flex items-center gap-2">
                          <i className="ri-eye-off-line"></i>
                          {language === 'ru' ? 'Скрыть товар' : language === 'es' ? 'Ocultar producto' : 'Hide Product'}
                        </div>
                        <div className="text-xs text-gray-600 montserrat mt-0.5">
                          {language === 'ru' ? 'Товар будет виден только администраторам' : language === 'es' ? 'El producto solo será visible para administradores' : 'Product will only be visible to admins'}
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Set Items Section */}
                  {productType === 'set' && (
                    <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border-2 border-blue-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium playfair flex items-center gap-2">
                          <i className="ri-list-check"></i>
                          {language === 'ru' ? 'Состав комплекта' : language === 'es' ? 'Artículos del conjunto' : 'Set Items'}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setSetItems([...setItems, { name: '', nameEn: '', nameEs: '', image: '', size: '', color: '' }])}
                          className="px-3 py-1.5 bg-black text-white rounded-lg text-xs montserrat hover:bg-neutral-800 transition-colors flex items-center gap-1"
                        >
                          <i className="ri-add-line"></i>
                          {language === 'ru' ? 'Добавить' : language === 'es' ? 'Añadir' : 'Add Item'}
                        </button>
                      </div>
                      <div className="space-y-4">
                        {setItems.map((item, idx) => (
                          <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200 relative">
                            {setItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setSetItems(setItems.filter((_, i) => i !== idx))}
                                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                              >
                                <i className="ri-close-line text-sm"></i>
                              </button>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                              <input
                                value={item.name}
                                onChange={(e) => {
                                  const updated = [...setItems]
                                  updated[idx].name = e.target.value
                                  setSetItems(updated)
                                }}
                                placeholder={language === 'ru' ? 'Название (RU)' : 'Name (RU)'}
                                className="border border-gray-300 p-2 rounded-lg montserrat text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                              />
                              <input
                                value={item.nameEn}
                                onChange={(e) => {
                                  const updated = [...setItems]
                                  updated[idx].nameEn = e.target.value
                                  setSetItems(updated)
                                }}
                                placeholder={language === 'ru' ? 'Название (EN)' : 'Name (EN)'}
                                className="border border-gray-300 p-2 rounded-lg montserrat text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                              />
                              <input
                                value={item.nameEs}
                                onChange={(e) => {
                                  const updated = [...setItems]
                                  updated[idx].nameEs = e.target.value
                                  setSetItems(updated)
                                }}
                                placeholder={language === 'ru' ? 'Название (ES)' : 'Name (ES)'}
                                className="border border-gray-300 p-2 rounded-lg montserrat text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                              />
                              <input
                                value={item.image}
                                onChange={(e) => {
                                  const updated = [...setItems]
                                  updated[idx].image = e.target.value
                                  setSetItems(updated)
                                }}
                                placeholder={language === 'ru' ? 'URL изображения' : 'Image URL'}
                                className="border border-gray-300 p-2 rounded-lg montserrat text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                value={item.size}
                                onChange={(e) => {
                                  const updated = [...setItems]
                                  updated[idx].size = e.target.value
                                  setSetItems(updated)
                                }}
                                placeholder={language === 'ru' ? 'Размер (опц.)' : 'Size (opt.)'}
                                className="border border-gray-300 p-2 rounded-lg montserrat text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                              />
                              <input
                                value={item.color}
                                onChange={(e) => {
                                  const updated = [...setItems]
                                  updated[idx].color = e.target.value
                                  setSetItems(updated)
                                }}
                                placeholder={language === 'ru' ? 'Цвет (опц.)' : 'Color (opt.)'}
                                className="border border-gray-300 p-2 rounded-lg montserrat text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <h3 className="text-sm font-medium mb-4 playfair flex items-center gap-2">
                      <i className="ri-file-text-line"></i>
                      {language === 'ru' ? 'Описания' : language === 'es' ? 'Descripciones' : 'Descriptions'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        value={newProduct.description}
                        onChange={(e)=>setNewProduct({...newProduct,description:e.target.value})}
                        placeholder={language === 'ru' ? 'Описание (RU)' : 'Description (RU)'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.descriptionEn}
                        onChange={(e)=>setNewProduct({...newProduct,descriptionEn:e.target.value})}
                        placeholder={language === 'ru' ? 'Описание (EN)' : 'Description (EN)'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.descriptionEs}
                        onChange={(e)=>setNewProduct({...newProduct,descriptionEs:e.target.value})}
                        placeholder={language === 'ru' ? 'Описание (ES)' : 'Description (ES)'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* Additional Product Details */}
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                    <h3 className="text-sm font-medium mb-4 playfair flex items-center gap-2">
                      <i className="ri-information-line"></i>
                      {language === 'ru' ? 'Дополнительные детали' : language === 'es' ? 'Detalles adicionales' : 'Additional Details'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        value={newProduct.stock}
                        onChange={(e)=>setNewProduct({...newProduct,stock:e.target.value})}
                        placeholder={language === 'ru' ? 'Количество на складе' : language === 'es' ? 'Stock' : 'Stock'}
                        type="number"
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.sku}
                        onChange={(e)=>setNewProduct({...newProduct,sku:e.target.value})}
                        placeholder={language === 'ru' ? 'Артикул (SKU)' : language === 'es' ? 'SKU' : 'SKU'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.brand}
                        onChange={(e)=>setNewProduct({...newProduct,brand:e.target.value})}
                        placeholder={language === 'ru' ? 'Бренд' : language === 'es' ? 'Marca' : 'Brand'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.badge}
                        onChange={(e)=>setNewProduct({...newProduct,badge:e.target.value})}
                        placeholder={language === 'ru' ? 'Бейдж (New, Exclusive)' : language === 'es' ? 'Badge' : 'Badge'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.material}
                        onChange={(e)=>setNewProduct({...newProduct,material:e.target.value})}
                        placeholder={language === 'ru' ? 'Материал' : language === 'es' ? 'Material' : 'Material'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.country}
                        onChange={(e)=>setNewProduct({...newProduct,country:e.target.value})}
                        placeholder={language === 'ru' ? 'Страна производства' : language === 'es' ? 'País' : 'Country'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.volume}
                        onChange={(e)=>setNewProduct({...newProduct,volume:e.target.value})}
                        placeholder={language === 'ru' ? 'Объем (для ароматов)' : language === 'es' ? 'Volumen' : 'Volume'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.weight}
                        onChange={(e)=>setNewProduct({...newProduct,weight:e.target.value})}
                        placeholder={language === 'ru' ? 'Вес' : language === 'es' ? 'Peso' : 'Weight'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.dimensions}
                        onChange={(e)=>setNewProduct({...newProduct,dimensions:e.target.value})}
                        placeholder={language === 'ru' ? 'Размеры' : language === 'es' ? 'Dimensiones' : 'Dimensions'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                      <input
                        value={newProduct.gender}
                        onChange={(e)=>setNewProduct({...newProduct,gender:e.target.value})}
                        placeholder={language === 'ru' ? 'Пол (Unisex, Men, Women)' : language === 'es' ? 'Género' : 'Gender'}
                        className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* Fragrance Notes (for perfumes) */}
                  {newProduct.category === 'fragrances' && (
                    <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-200">
                      <h3 className="text-sm font-medium mb-4 playfair flex items-center gap-2">
                        <i className="ri-flask-line"></i>
                        {language === 'ru' ? 'Ноты аромата' : language === 'es' ? 'Notas de fragancia' : 'Fragrance Notes'}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                          value={newProduct.notes_top}
                          onChange={(e)=>setNewProduct({...newProduct,notes_top:e.target.value})}
                          placeholder={language === 'ru' ? 'Верхние ноты' : language === 'es' ? 'Notas de salida' : 'Top Notes'}
                          className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                        <input
                          value={newProduct.notes_heart}
                          onChange={(e)=>setNewProduct({...newProduct,notes_heart:e.target.value})}
                          placeholder={language === 'ru' ? 'Сердечные ноты' : language === 'es' ? 'Notas de corazón' : 'Heart Notes'}
                          className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                        <input
                          value={newProduct.notes_base}
                          onChange={(e)=>setNewProduct({...newProduct,notes_base:e.target.value})}
                          placeholder={language === 'ru' ? 'Базовые ноты' : language === 'es' ? 'Notas de fondo' : 'Base Notes'}
                          className="border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                  )}

                  {/* Sizes and Colors */}
                  <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-200">
                    <h3 className="text-sm font-medium mb-4 playfair flex items-center gap-2">
                      <i className="ri-ruler-line"></i>
                      {language === 'ru' ? 'Размеры и цвета' : language === 'es' ? 'Tallas y colores' : 'Sizes & Colors'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm mb-2 montserrat">
                          {language === 'ru' ? 'Размеры (через запятую)' : language === 'es' ? 'Tallas (separadas por comas)' : 'Sizes (comma-separated)'}
                        </label>
                        <input
                          value={Array.isArray(newProduct.sizes) ? newProduct.sizes.join(', ') : ''}
                          onChange={(e)=>setNewProduct({...newProduct,sizes:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})}
                          placeholder="XS, S, M, L, XL"
                          className="w-full border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm mb-2 montserrat">
                          {language === 'ru' ? 'Цвета (через запятую)' : language === 'es' ? 'Colores (separados por comas)' : 'Colors (comma-separated)'}
                        </label>
                        <input
                          value={Array.isArray(newProduct.colors) ? newProduct.colors.join(', ') : ''}
                          onChange={(e)=>setNewProduct({...newProduct,colors:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})}
                          placeholder={language === 'ru' ? 'Черный, Белый, Красный' : language === 'es' ? 'Negro, Blanco, Rojo' : 'Black, White, Red'}
                          className="w-full border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Images Gallery */}
                  <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-xl border border-green-200">
                    <h3 className="text-sm font-medium mb-4 playfair flex items-center gap-2">
                      <i className="ri-gallery-line"></i>
                      {language === 'ru' ? 'Дополнительные изображения' : language === 'es' ? 'Imágenes adicionales' : 'Additional Images'}
                    </h3>
                    <div>
                      <label className="block text-sm mb-2 montserrat">
                        {language === 'ru' ? 'URL изображений (через запятую)' : language === 'es' ? 'URLs de imágenes (separadas por comas)' : 'Image URLs (comma-separated)'}
                      </label>
                      <textarea
                        value={Array.isArray(newProduct.images) ? newProduct.images.join(', ') : ''}
                        onChange={(e)=>setNewProduct({...newProduct,images:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})}
                        placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                        rows={3}
                        className="w-full border border-gray-300 p-3 rounded-lg montserrat focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-4 bg-black text-white rounded-lg montserrat hover:bg-neutral-900 transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <i className="ri-add-line text-xl"></i>
                    {language === 'ru' ? 'Создать товар' : language === 'es' ? 'Crear producto' : 'Create Product'}
                  </button>
                </form>
              </div>
            )}

            {/* Manage Products Tab (Admin Only) */}
            {activeTab === 'manage-products' && isAdmin && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 mb-6">
                  <i className="ri-edit-box-line text-2xl"></i>
                  <h2 className="text-xl font-medium playfair">
                    {language === 'ru' ? 'Управление товарами' : language === 'es' ? 'Gestionar productos' : 'Manage Products'}
                  </h2>
                </div>

                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 montserrat flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {submitError}
                  </div>
                )}
                {submitInfo && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 montserrat flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    {submitInfo}
                  </div>
                )}

                {loadingProducts ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                  </div>
                ) : allProducts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 montserrat">
                    {language === 'ru' ? 'Нет товаров' : language === 'es' ? 'No hay productos' : 'No products'}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allProducts.map((product) => (
                      <div key={product.id} className={`bg-white border rounded-xl overflow-hidden transition-all ${
                        product.is_hidden ? 'border-red-200 bg-red-50/30' : 'border-gray-200'
                      }`}>
                        {editingProduct === product.id ? (
                          // Edit Mode
                          <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="font-medium playfair">
                                {language === 'ru' ? 'Редактирование' : language === 'es' ? 'Editando' : 'Editing'} #{product.id}
                              </h3>
                              <button
                                onClick={() => {
                                  setEditingProduct(null)
                                  setEditForm(null)
                                }}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <i className="ri-close-line text-xl"></i>
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <input
                                value={editForm.name}
                                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                placeholder="Name (RU)"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                              <input
                                value={editForm.nameEn}
                                onChange={(e) => setEditForm({...editForm, nameEn: e.target.value})}
                                placeholder="Name (EN)"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                              <input
                                value={editForm.nameEs}
                                onChange={(e) => setEditForm({...editForm, nameEs: e.target.value})}
                                placeholder="Name (ES)"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <input
                                value={editForm.price}
                                onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                                placeholder="Price"
                                type="number"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                              <input
                                value={editForm.stock}
                                onChange={(e) => setEditForm({...editForm, stock: e.target.value})}
                                placeholder="Stock"
                                type="number"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                              <select
                                value={editForm.category}
                                onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              >
                                {categories.filter(c=>c.id!=='all').map(c => (
                                  <option key={c.id} value={c.id}>{c.name[language]}</option>
                                ))}
                              </select>
                              <select
                                value={editForm.collection}
                                onChange={(e) => setEditForm({...editForm, collection: e.target.value})}
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              >
                                {collections.filter(c=>c.id!=='all').map(c => (
                                  <option key={c.id} value={c.id}>{c.name[language]}</option>
                                ))}
                              </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <input
                                value={editForm.description}
                                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                                placeholder="Description (RU)"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                              <input
                                value={editForm.descriptionEn}
                                onChange={(e) => setEditForm({...editForm, descriptionEn: e.target.value})}
                                placeholder="Description (EN)"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                              <input
                                value={editForm.descriptionEs}
                                onChange={(e) => setEditForm({...editForm, descriptionEs: e.target.value})}
                                placeholder="Description (ES)"
                                className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                              />
                            </div>

                            {/* Additional Details */}
                            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                              <h4 className="text-sm font-medium montserrat mb-2">
                                {language === 'ru' ? 'Дополнительные детали' : language === 'es' ? 'Detalles adicionales' : 'Additional Details'}
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                <input
                                  value={editForm.sku || ''}
                                  onChange={(e) => setEditForm({...editForm, sku: e.target.value})}
                                  placeholder="SKU"
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.brand || ''}
                                  onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
                                  placeholder={language === 'ru' ? 'Бренд' : 'Brand'}
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.badge || ''}
                                  onChange={(e) => setEditForm({...editForm, badge: e.target.value})}
                                  placeholder="Badge"
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.material || ''}
                                  onChange={(e) => setEditForm({...editForm, material: e.target.value})}
                                  placeholder={language === 'ru' ? 'Материал' : 'Material'}
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.country || ''}
                                  onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                                  placeholder={language === 'ru' ? 'Страна' : 'Country'}
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.volume || ''}
                                  onChange={(e) => setEditForm({...editForm, volume: e.target.value})}
                                  placeholder={language === 'ru' ? 'Объем' : 'Volume'}
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.weight || ''}
                                  onChange={(e) => setEditForm({...editForm, weight: e.target.value})}
                                  placeholder={language === 'ru' ? 'Вес' : 'Weight'}
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.dimensions || ''}
                                  onChange={(e) => setEditForm({...editForm, dimensions: e.target.value})}
                                  placeholder={language === 'ru' ? 'Размеры' : 'Dimensions'}
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                                <input
                                  value={editForm.gender || ''}
                                  onChange={(e) => setEditForm({...editForm, gender: e.target.value})}
                                  placeholder={language === 'ru' ? 'Пол' : 'Gender'}
                                  className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                              </div>
                            </div>

                            {/* Fragrance Notes */}
                            {editForm.category === 'fragrances' && (
                              <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                                <h4 className="text-sm font-medium montserrat mb-2">
                                  {language === 'ru' ? 'Ноты аромата' : language === 'es' ? 'Notas de fragancia' : 'Fragrance Notes'}
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <input
                                    value={editForm.notes_top || ''}
                                    onChange={(e) => setEditForm({...editForm, notes_top: e.target.value})}
                                    placeholder={language === 'ru' ? 'Верхние ноты' : 'Top Notes'}
                                    className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                  />
                                  <input
                                    value={editForm.notes_heart || ''}
                                    onChange={(e) => setEditForm({...editForm, notes_heart: e.target.value})}
                                    placeholder={language === 'ru' ? 'Сердечные ноты' : 'Heart Notes'}
                                    className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                  />
                                  <input
                                    value={editForm.notes_base || ''}
                                    onChange={(e) => setEditForm({...editForm, notes_base: e.target.value})}
                                    placeholder={language === 'ru' ? 'Базовые ноты' : 'Base Notes'}
                                    className="border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                  />
                                </div>
                              </div>
                            )}

                            {/* Sizes and Colors */}
                            <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                              <h4 className="text-sm font-medium montserrat mb-2">
                                {language === 'ru' ? 'Размеры и цвета' : language === 'es' ? 'Tallas y colores' : 'Sizes & Colors'}
                              </h4>
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs mb-1 montserrat text-gray-600">
                                    {language === 'ru' ? 'Размеры (через запятую)' : 'Sizes (comma-separated)'}
                                  </label>
                                  <input
                                    value={editForm.sizesText || ''}
                                    onChange={(e) => setEditForm({...editForm, sizesText: e.target.value})}
                                    placeholder="XS, S, M, L, XL"
                                    className="w-full border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs mb-1 montserrat text-gray-600">
                                    {language === 'ru' ? 'Цвета (через запятую)' : 'Colors (comma-separated)'}
                                  </label>
                                  <input
                                    value={editForm.colorsText || ''}
                                    onChange={(e) => setEditForm({...editForm, colorsText: e.target.value})}
                                    placeholder={language === 'ru' ? 'Черный, Белый' : 'Black, White'}
                                    className="w-full border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Additional Images */}
                            <div className="bg-green-50 p-4 rounded-lg space-y-3">
                              <h4 className="text-sm font-medium montserrat mb-2">
                                {language === 'ru' ? 'Дополнительные изображения' : language === 'es' ? 'Imágenes adicionales' : 'Additional Images'}
                              </h4>
                              <div>
                                <label className="block text-xs mb-1 montserrat text-gray-600">
                                  {language === 'ru' ? 'URL изображений (через запятую)' : 'Image URLs (comma-separated)'}
                                </label>
                                <textarea
                                  value={editForm.imagesText || ''}
                                  onChange={(e) => setEditForm({...editForm, imagesText: e.target.value})}
                                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                                  rows={3}
                                  className="w-full border border-gray-300 p-2 rounded-lg text-sm montserrat"
                                />
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editForm.is_hidden}
                                  onChange={(e) => setEditForm({...editForm, is_hidden: e.target.checked})}
                                  className="w-4 h-4 rounded"
                                />
                                <span className="text-sm montserrat">
                                  <i className="ri-eye-off-line mr-1"></i>
                                  {language === 'ru' ? 'Скрыть' : language === 'es' ? 'Ocultar' : 'Hide'}
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editForm.is_set}
                                  onChange={(e) => setEditForm({...editForm, is_set: e.target.checked})}
                                  className="w-4 h-4 rounded"
                                />
                                <span className="text-sm montserrat">
                                  <i className="ri-stack-fill mr-1"></i>
                                  {language === 'ru' ? 'Комплект' : language === 'es' ? 'Conjunto' : 'Set'}
                                </span>
                              </label>
                            </div>

                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={() => handleUpdateProduct(product.id)}
                                className="flex-1 px-4 py-2 bg-black text-white rounded-lg montserrat hover:bg-neutral-800 transition-colors text-sm"
                              >
                                <i className="ri-save-line mr-2"></i>
                                {language === 'ru' ? 'Сохранить' : language === 'es' ? 'Guardar' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingProduct(null)
                                  setEditForm(null)
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg montserrat hover:bg-gray-50 transition-colors text-sm"
                              >
                                {language === 'ru' ? 'Отмена' : language === 'es' ? 'Cancelar' : 'Cancel'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="flex gap-4 p-4">
                            <MediaWithShimmer
                              src={Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : (product.image || '')}
                              alt={product.name}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium playfair truncate">{product.name}</h3>
                                  <p className="text-sm text-gray-600 montserrat">ID: {product.id}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {product.is_hidden && (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs montserrat">
                                      <i className="ri-eye-off-line"></i>
                                    </span>
                                  )}
                                  {product.is_set && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs montserrat">
                                      <i className="ri-stack-fill"></i>
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 text-sm montserrat text-gray-600 mb-3">
                                <span>{new Intl.NumberFormat('ru-RU').format(product.price).replace(/\s/g, '\u00A0') + '\u00A0€'}</span>
                                <span>•</span>
                                <span>{language === 'ru' ? 'Склад' : language === 'es' ? 'Stock' : 'Stock'}: {product.stock || 0}</span>
                                <span>•</span>
                                <span>{categories.find(c => c.id === product.category)?.name[language]}</span>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditProduct(product)}
                                  className="px-3 py-1.5 bg-black text-white rounded-lg text-sm montserrat hover:bg-neutral-800 transition-colors"
                                >
                                  <i className="ri-edit-line mr-1"></i>
                                  {language === 'ru' ? 'Изменить' : language === 'es' ? 'Editar' : 'Edit'}
                                </button>
                                <button
                                  onClick={() => handleDeleteProduct(product.id)}
                                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm montserrat hover:bg-red-700 transition-colors"
                                >
                                  <i className="ri-delete-bin-line mr-1"></i>
                                  {language === 'ru' ? 'Удалить' : language === 'es' ? 'Eliminar' : 'Delete'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

export default Account
